import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Youtube } from "lucide-react";
import { VIDEOS } from "../../lib/content";
import VideoLightbox from "./VideoLightbox";

// Compact YouTube/video strip on the Home page.
// - Shows the 3 most recent items from VIDEOS (sorted by date desc).
// - Clicking a card opens the universal VideoLightbox (no nav away).
// - "View all videos" deep-links to /insights?tab=videos.
// - Hides itself entirely when no videos are published.
export default function HomeVideoStrip() {
  const [active, setActive] = useState(null);

  const items = useMemo(() => {
    // Curated picks first (entries with home_strip: true). Fall back to the
    // most-recent 3 if there are fewer than 3 curated picks.
    const pinned = VIDEOS.filter((v) => v.home_strip);
    if (pinned.length >= 3) return pinned.slice(0, 3);
    const rest = VIDEOS
      .filter((v) => !v.home_strip)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return [...pinned, ...rest].slice(0, 3);
  }, []);

  if (items.length === 0) return null;

  return (
    <section
      className="px-6 md:px-12 lg:px-20 py-10 md:py-14"
      style={{ background: "#070A0D", color: "#F5F7FA" }}
      data-testid="home-video-strip"
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-5 mb-6 md:mb-8">
          <div>
            <div
              className="font-mono uppercase inline-flex items-center gap-2 mb-2"
              style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1" }}
            >
              <Youtube size={11} aria-hidden /> From the field
            </div>
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(1.3rem, 2.4vw, 1.9rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
                fontWeight: 400,
                maxWidth: "28ch",
              }}
            >
              Shorts from the plant rooms and the team behind them.
            </h2>
          </div>
          <Link
            to="/insights?tab=videos"
            className="text-[0.78rem] font-mono uppercase inline-flex items-center gap-1.5 transition-colors hover:text-[#1FB6C1]"
            style={{ letterSpacing: "0.18em", color: "#F5F7FA" }}
            data-testid="home-video-view-all"
          >
            View all videos <span aria-hidden>→</span>
          </Link>
        </div>

        {/* MOBILE: 3-up grid of compact thumbnails.
            DESKTOP: same 3-up grid, each column splits into thumb-left + caption-right. */}
        <div
          className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4"
          data-testid="home-video-grid"
        >
          {items.map((v) => (
            <HomeVideoCard key={v.slug} video={v} onPlay={() => setActive(v)} />
          ))}
        </div>
      </div>

      <VideoLightbox video={active} onClose={() => setActive(null)} />
    </section>
  );
}

function HomeVideoCard({ video: v, onPlay }) {
  const isPortrait = v.orientation === "portrait";
  const poster =
    v.poster ||
    (v.youtube_id ? `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg` : "/library/purification/purification_01.webp");

  return (
    <div className="flex md:flex-row flex-col gap-3 items-stretch min-w-0">
      {/* THUMBNAIL — clickable. Restored to original compact sizing. */}
      <button
        type="button"
        onClick={onPlay}
        className="group relative block overflow-hidden text-left transition-transform duration-300 hover:-translate-y-1 shrink-0 w-full md:w-[170px] lg:w-[210px]"
        style={{
          borderRadius: 6,
          aspectRatio: isPortrait ? "9 / 16" : "16 / 9",
          background: "#0C2A40",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
        data-testid={`home-video-card-${v.slug}`}
        aria-label={`Play video: ${v.title}`}
      >
        <img
          src={poster}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = "/library/purification/purification_01.webp"; }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(7,10,13,0.10) 30%, rgba(7,10,13,0.78) 100%)" }}
          aria-hidden
        />

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              background: "rgba(255,255,255,0.96)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.40)",
            }}
          >
            <Play size={16} fill="#0C2A40" color="#0C2A40" style={{ marginLeft: 2 }} aria-hidden />
          </div>
        </div>

        {/* Duration badge */}
        {v.duration && (
          <div
            className="absolute top-2 right-2 px-1.5 py-0.5 text-[0.6rem] font-semibold"
            style={{
              background: "rgba(7,10,13,0.85)",
              color: "#F5F7FA",
              borderRadius: 3,
              letterSpacing: "0.02em",
            }}
          >
            {v.duration}
          </div>
        )}

        {/* MOBILE-ONLY title overlay on thumbnail (desktop shows caption beside instead) */}
        <div className="md:hidden absolute left-2.5 right-2.5 bottom-2.5">
          <div
            className="font-display text-white"
            style={{
              fontSize: "0.78rem",
              lineHeight: 1.25,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              textShadow: "0 2px 12px rgba(0,0,0,0.7)",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {v.title}
          </div>
          {v.filmed_at && (
            <div
              className="mt-1 font-mono uppercase"
              style={{
                fontSize: "0.5rem",
                letterSpacing: "0.18em",
                color: "rgba(245,247,250,0.78)",
              }}
            >
              {v.filmed_at}
            </div>
          )}
        </div>
      </button>

      {/* DESKTOP-ONLY caption beside the thumbnail — flows vertically to match thumbnail height. */}
      <div className="hidden md:flex flex-col justify-start flex-1 min-w-0 pr-2 pt-1">
        {v.filmed_at && (
          <div
            className="font-mono uppercase mb-2"
            style={{
              fontSize: "0.54rem",
              letterSpacing: "0.22em",
              color: "#1FB6C1",
            }}
          >
            {v.filmed_at}
          </div>
        )}
        <h3
          className="font-display mb-2"
          style={{
            fontSize: "0.95rem",
            lineHeight: 1.25,
            letterSpacing: "-0.005em",
            color: "#F5F7FA",
            fontWeight: 600,
          }}
        >
          {v.title}
        </h3>
        <p
          className="font-light"
          style={{
            fontSize: "0.78rem",
            lineHeight: 1.55,
            color: "rgba(245,247,250,0.7)",
          }}
        >
          {v.caption}
        </p>
      </div>
    </div>
  );
}

// Desktop tile — horizontal layout. Thumb on the left at portrait aspect,
// caption + title + filmed_at fills the right two-thirds of the row.
// (Unused — kept temporarily for reference, will be removed.)

